import { useState, useEffect, useCallback } from 'react'
import { api } from '../services/api'

export interface ExchangeRate {
  source: 'comercial' | 'ptax'
  buy: number
  sell: number
  timestamp: string
  date: string
}

// PreferredCurrency for settings (BRL/USD only for now, as we only have USD/BRL exchange rate)
export type PreferredCurrency = 'BRL' | 'USD'

// All supported currencies for offers
export type OfferCurrency = 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CNY'

interface UseExchangeRateOptions {
  autoRefresh?: boolean
  refreshInterval?: number // milliseconds
  source?: 'comercial' | 'ptax' | 'preferred' // 'preferred' tries FF preference first
  profileType?: 'shipper' | 'freight-forwarder' // Which profile's settings to use for currency preference
}

interface UseExchangeRateReturn {
  rate: ExchangeRate | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  convertUSDToBRL: (usdAmount: number) => number
  convertBRLToUSD: (brlAmount: number) => number
  preferredCurrency: PreferredCurrency
  setPreferredCurrency: (currency: PreferredCurrency) => void
}

/**
 * Hook to fetch and manage exchange rate (USD to BRL)
 *
 * Features:
 * - Automatic refresh at intervals
 * - Caching to avoid excessive API calls
 * - Helper functions for currency conversion
 * - Error handling and retry logic
 *
 * @example
 * const { rate, loading, convertUSDToBRL } = useExchangeRate()
 * const brlValue = convertUSDToBRL(100) // 100 USD to BRL
 */
export function useExchangeRate(options: UseExchangeRateOptions = {}): UseExchangeRateReturn {
  const {
    autoRefresh = true,
    refreshInterval = 5 * 60 * 1000, // 5 minutes default
    source = 'comercial',
    profileType,
  } = options

  const [rate, setRate] = useState<ExchangeRate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [preferredCurrency, setPreferredCurrencyState] = useState<PreferredCurrency>('BRL')

  // Fetch preferred currency from settings based on profile type
  useEffect(() => {
    async function fetchPreferredCurrency() {
      // If profile type is specified, only try that endpoint
      if (profileType === 'shipper') {
        try {
          const response = await api.get('/shipper/settings')
          const settings = response.data.settings?.settings
          if (settings?.preferredCurrency) {
            setPreferredCurrencyState(settings.preferredCurrency)
          }
        } catch {
          // Keep default (BRL)
        }
        return
      }

      if (profileType === 'freight-forwarder') {
        try {
          const response = await api.get('/freight-forwarder/settings')
          const settings = response.data.settings?.settings
          if (settings?.preferredCurrency) {
            setPreferredCurrencyState(settings.preferredCurrency)
          }
        } catch {
          // Keep default (BRL)
        }
        return
      }

      // No profile specified - try freight forwarder first, then shipper
      try {
        const response = await api.get('/freight-forwarder/settings')
        const settings = response.data.settings?.settings
        if (settings?.preferredCurrency) {
          setPreferredCurrencyState(settings.preferredCurrency)
        }
      } catch {
        // Try shipper settings as fallback
        try {
          const response = await api.get('/shipper/settings')
          const settings = response.data.settings?.settings
          if (settings?.preferredCurrency) {
            setPreferredCurrencyState(settings.preferredCurrency)
          }
        } catch {
          // Keep default (BRL)
        }
      }
    }
    fetchPreferredCurrency()
  }, [profileType])

  const setPreferredCurrency = useCallback((currency: PreferredCurrency) => {
    setPreferredCurrencyState(currency)
  }, [])

  const fetchRate = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      let response

      if (source === 'preferred') {
        // Try freight forwarder's preferred rate first
        try {
          response = await api.get('/freight-forwarder/exchange-rate')
        } catch {
          // Fallback to commercial rate
          response = await api.get('/exchange-rates/comercial')
        }
      } else {
        // Use specified source
        response = await api.get(`/exchange-rates/${source}`)
      }

      if (response.data?.rate) {
        setRate(response.data.rate)
      } else {
        throw new Error('Invalid response format')
      }
    } catch (err: any) {
      console.error('Failed to fetch exchange rate:', err)
      setError(err.response?.data?.error || 'Erro ao carregar taxa de câmbio')
    } finally {
      setLoading(false)
    }
  }, [source])

  // Initial fetch
  useEffect(() => {
    fetchRate()
  }, [fetchRate])

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh || refreshInterval <= 0) return

    const interval = setInterval(fetchRate, refreshInterval)
    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, fetchRate])

  /**
   * Convert USD amount to BRL using current rate
   */
  const convertUSDToBRL = useCallback(
    (usdAmount: number): number => {
      if (!rate || !usdAmount) return 0
      return usdAmount * rate.sell // Use sell rate for USD to BRL
    },
    [rate]
  )

  /**
   * Convert BRL amount to USD using current rate
   */
  const convertBRLToUSD = useCallback(
    (brlAmount: number): number => {
      if (!rate || !brlAmount) return 0
      return brlAmount / rate.buy // Use buy rate for BRL to USD
    },
    [rate]
  )

  return {
    rate,
    loading,
    error,
    refresh: fetchRate,
    convertUSDToBRL,
    convertBRLToUSD,
    preferredCurrency,
    setPreferredCurrency,
  }
}

// Supported currencies
export type CurrencyCode = 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CNY'

// Currency locale mapping
const CURRENCY_LOCALES: Record<CurrencyCode, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  CNY: 'zh-CN',
}

/**
 * Format currency value with locale
 * Supports: BRL, USD, EUR, GBP, CNY
 */
export function formatCurrency(value: number, currency: CurrencyCode | string): string {
  const currencyCode = currency as CurrencyCode
  const locale = CURRENCY_LOCALES[currencyCode] || 'pt-BR'

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format compact currency (e.g., 1.5K, 2.3M)
 * Supports: BRL, USD, EUR, GBP, CNY
 */
export function formatCompactCurrency(value: number, currency: CurrencyCode | string): string {
  const currencyCode = currency as CurrencyCode
  const locale = CURRENCY_LOCALES[currencyCode] || 'pt-BR'

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    notation: 'compact',
    compactDisplay: 'short',
  })

  return formatter.format(value)
}
